import { CheckCircleOutlined, QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { Skeleton } from 'antd'
import type { JSX } from 'react'
import styled from 'styled-components'

import { ATTENTION, MUTED_TEXT, TEXT, TONES } from '../../colors'
import { SettingsPage } from '../Settings'
import {
  StyledCard,
  StyledCardBody,
  StyledLinkButton,
  StyledRow,
  StyledRowDescription,
  StyledRowLabel,
  StyledRowText,
} from '../Settings/styled'
import { StatusDot } from '../StatusBadge'

import { HealthTiles } from './HealthTiles'
import { KeySettings } from './KeySettings'
import { LatestCampaigns } from './LatestCampaigns'
import { RecentChanges } from './RecentChanges'
import { ClientHealth } from './useClientHealth'

import { Client } from 'api/clients'
import { route } from 'utils/router'

const StyledAttentionCard = styled(StyledCard)`
  border-color: ${ATTENTION.border};
`

type PanelVariant = 'attention' | 'clear' | 'unchecked'

const PANEL_ICON_COLORS: Record<PanelVariant, string> = {
  attention: ATTENTION.icon,
  clear: TONES.success.dot,
  unchecked: MUTED_TEXT,
}

const StyledPanelHeader = styled.div<{ $variant: PanelVariant }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-radius: 8px 8px 0 0;
  background: ${({ $variant }) => ($variant === 'attention' ? ATTENTION.headerBg : 'transparent')};
  color: ${({ $variant }) => PANEL_ICON_COLORS[$variant]};
  font-size: 18px;

  & + * {
    border-top: 1px solid ${({ $variant }) => ($variant === 'attention' ? ATTENTION.headerBorder : 'transparent')};
  }

  h3 {
    margin: 0;
    color: ${({ $variant }) => ($variant === 'attention' ? TONES.warning.fg : TEXT)};
    font-size: 16px;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: ${MUTED_TEXT};
    font-size: 13px;
  }
`

const StyledFailedChecks = styled.p`
  margin: 0;
  padding: 0 24px 16px;
  color: ${TONES.error.fg};
  font-size: 13px;
`

const StyledColumns = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px;

  > :first-child {
    flex: 3 1 420px;
  }

  > :last-child {
    flex: 2 1 260px;
  }
`

function FailedChecks({ checks }: { checks: string[] }): JSX.Element | null {
  if (checks.length === 0) return null
  return (
    <StyledFailedChecks>Could not check {checks.join(', ')}. Open those sections to check them.</StyledFailedChecks>
  )
}

function AttentionPanel({ clientId, health }: { clientId: string; health: ClientHealth }): JSX.Element {
  const { attentionItems, failedChecks, isLoading } = health

  if (attentionItems.length === 0 && isLoading) {
    return (
      <StyledCard aria-busy="true">
        <StyledPanelHeader $variant="clear">
          <Skeleton active paragraph={false} title={{ width: '40%' }} />
        </StyledPanelHeader>
      </StyledCard>
    )
  }

  if (attentionItems.length === 0 && failedChecks.length > 0) {
    return (
      <StyledCard aria-labelledby="client-attention">
        <StyledPanelHeader $variant="unchecked">
          <QuestionCircleOutlined aria-hidden="true" />
          <div>
            <h3 id="client-attention">Some checks could not run</h3>
            <p>The other health checks that apply to this client found no problems.</p>
          </div>
        </StyledPanelHeader>
        <FailedChecks checks={failedChecks} />
      </StyledCard>
    )
  }

  if (attentionItems.length === 0) {
    return (
      <StyledCard aria-labelledby="client-attention">
        <StyledPanelHeader $variant="clear">
          <CheckCircleOutlined aria-hidden="true" />
          <div>
            <h3 id="client-attention">Nothing needs attention</h3>
            <p>The health checks that apply to this client found no problems.</p>
          </div>
        </StyledPanelHeader>
      </StyledCard>
    )
  }

  return (
    <StyledAttentionCard aria-labelledby="client-attention">
      <StyledPanelHeader $variant="attention">
        <WarningOutlined aria-hidden="true" />
        <h3 id="client-attention">Needs attention · {attentionItems.length}</h3>
      </StyledPanelHeader>
      <StyledCardBody>
        {attentionItems.map((item) => (
          <StyledRow key={item.id}>
            <StyledRowText>
              <StyledRowLabel>
                <StatusDot $tone={item.tone} aria-hidden="true" />
                {item.title}
              </StyledRowLabel>
              <StyledRowDescription>{item.description}</StyledRowDescription>
            </StyledRowText>
            <StyledLinkButton to={route(item.route, { id: clientId })}>{item.actionLabel}</StyledLinkButton>
          </StyledRow>
        ))}
      </StyledCardBody>
      <FailedChecks checks={failedChecks} />
    </StyledAttentionCard>
  )
}

export function Overview({ client, health }: { client: Client; health: ClientHealth }): JSX.Element {
  return (
    <SettingsPage
      description="Health of this client’s account, numbers and compliance, and the settings that staff change most."
      title="Overview"
    >
      <AttentionPanel clientId={client.id} health={health} />
      <HealthTiles client={client} health={health} />
      <LatestCampaigns clientId={client.id} />
      <StyledColumns>
        <KeySettings client={client} />
        <RecentChanges clientId={client.id} />
      </StyledColumns>
    </SettingsPage>
  )
}
